import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { faChartLine } from '@fortawesome/pro-duotone-svg-icons';
import { Select, Store } from '@ngxs/store';
import _ from 'lodash';
import ResizeObserver from 'resize-observer-polyfill';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { AgentDetailsBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-details-behavior/agent-details-behavior.state';
import {
   LoadingDashboardCompleted,
   LoadingDashboardStarted,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/grafana-dashboard-viewer-behaviour/grafana-dashboard-viewer-behaviour.actions';
import { GrafanaDashboardViewerBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/grafana-dashboard-viewer-behaviour/grafana-dashboard-viewer-behaviour.state';
import { CommonBrickSettingsSelectors } from 'src/app/store/dashboard/brick-wall/brick-settings/common-brick-settings/common-brick-settings.selectors';
import {
   FOLLOW_SELECTED_AGENT,
   GrafanaDashboardViewerSettingsState,
} from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/grafana-dashboard-viewer-brick-settings/grafana-dashboard-viewer-brick-settings.state';
import { SettingsState } from 'src/app/store/settings/settings.state';
import { SamwebWindowState } from 'src/app/store/utility/samweb-window/samweb-window.state';

import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { GrafanaDashboardViewerSettingsComponent } from './grafana-dashboard-viewer-settings/grafana-dashboard-viewer-settings.component';
import { IframeDimensions } from './iframe-dimensions';

@Component({
   selector: 'samweb-grafana-dashboard-viewer-brick',
   templateUrl: './grafana-dashboard-viewer-brick.component.html',
   styleUrls: ['./grafana-dashboard-viewer-brick.component.scss'],
})
@SamwebBrick({
   brickDefinitionId: 'grafanaDashboardViewer',
   brickTitleTranslationKey: 'brick.brick_definitions.grafana_dashboard_viewer.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.grafana_dashboard_viewer.description',
   brickIcon: faChartLine,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.GENERAL,
   settingsComponent: GrafanaDashboardViewerSettingsComponent,
   settingStateHandler: GrafanaDashboardViewerSettingsState,
})
export class GrafanaDashboardViewerBrickComponent extends BasicBrickComponent implements OnInit, AfterViewInit, OnDestroy {
   @Select(SamwebWindowState.isWindowDraggedOrResized)
   public isWindowDraggedOrResized$: Observable<boolean>;

   @ViewChildren('iframe')
   public iframes: QueryList<ElementRef>;

   public dashboardIcon = faChartLine;

   public hasConfiguration$: Observable<boolean>;
   public url$: Observable<SafeUrl>;
   public isLoading$: Observable<boolean>;

   public iframe: ElementRef;
   private iframeDimensions: IframeDimensions;
   private resizeObserver: ResizeObserver;
   private resizeDelayTimer: NodeJS.Timer;
   private iframeStartedInitializingTimestamp: Date;
   private iframeProbablyCouldNotFullyLoad: boolean;
   private destroy$ = new Subject();

   constructor(private store: Store, private sanitizer: DomSanitizer) {
      super();
   }

   ngOnInit(): void {
      this.isLoading$ = this.store.select(GrafanaDashboardViewerBehaviourState.getIsLoading).pipe(map((fn) => fn(this.brickInstanceId)));
      this.hasConfiguration$ = this.store
         .select(GrafanaDashboardViewerSettingsState.getDashboardSettingsFn)
         .pipe(map((fn) => fn(this.brickInstanceId).dashboardId > 0));

      this.url$ = combineLatest([
         this.store.select(GrafanaDashboardViewerSettingsState.getDashboardSettingsFn).pipe(map((fn) => fn(this.brickInstanceId))),
         this.store.select(AgentDetailsBehaviourState.getCurrentSelectedAgentId),
         this.store.select(SettingsState.appliedTheme),
         this.store.select(SettingsState.getCurrentLanguage),
         this.store.select(CommonBrickSettingsSelectors.getEffectiveBrickScalingFn),
      ]).pipe(
         filter(([settings]) => settings.dashboardId > 0),
         switchMap(([settings, currentSelectedAgentId, theme, language, getEffectiveBrickScalingFn]) => {
            let url = './samwinManager/Dashboards/DashboardViewer.aspx';
            url += `?id=${settings.dashboardId}`;
            url += `&theme=${theme.grafanaThemeName}`;
            url += `&language=${language.grafanaLanguageCode}`;
            if (settings.agentId > 0) {
               url += `&user=${settings.agentId}`;
            } else if (settings.agentId === FOLLOW_SELECTED_AGENT && currentSelectedAgentId > 0) {
               url += `&user=${currentSelectedAgentId}`;
            }

            url += '&scale=' + getEffectiveBrickScalingFn(this.brickInstanceId) / 100;

            if (settings.useBrowserTimezoneAsTargetTimezone) {
               const ianaTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
               url += `&targetTz=${encodeURIComponent(ianaTimezone)}`;
            }

            this.iframeStartedInitializingTimestamp = new Date();

            return of(url);
         }),
         distinctUntilChanged(),
         switchMap((url) => of(this.sanitizer.bypassSecurityTrustResourceUrl(url))),
         tap(() => {
            this.store.dispatch(new LoadingDashboardStarted(this.brickInstanceId));
            this.iframeDimensions = this.getIframeDimensions();
         })
      );
   }

   ngAfterViewInit(): void {
      this.iframeDimensions = this.getIframeDimensions();
      this.resizeObserver = new ResizeObserver(() => {
         if (!_.isNil(this.resizeDelayTimer)) clearTimeout(this.resizeDelayTimer);

         this.resizeDelayTimer = setTimeout(() => this.handleIframeSizeChange(), 150);
      });

      this.updateIframe(this.iframes);

      // https://github.com/angular/angular/issues/17769#issuecomment-357281999
      this.iframes.changes.pipe(takeUntil(this.destroy$)).subscribe((components: QueryList<ElementRef>) => {
         this.updateIframe(components);
      });
   }

   ngOnDestroy(): void {
      if (this.resizeObserver) this.resizeObserver.disconnect();

      this.destroy$.next();
      this.destroy$.complete();
   }

   loadingCompleted() {
      this.store.dispatch(new LoadingDashboardCompleted(this.brickInstanceId));
   }

   private updateIframe(iframes: QueryList<ElementRef<any>>): void {
      if (_.isNil(iframes) || iframes.length === 0 || _.isNil(iframes.first)) return;

      this.iframe = iframes.first;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.resizeObserver.observe(this.iframe.nativeElement);
   }

   private handleIframeSizeChange(): void {
      const currentTimestamp = new Date();
      const newIframeDimensions = this.getIframeDimensions();

      if (newIframeDimensions.isZeroSize()) {
         // if the brick was only visible for such a short amount of time, the dashboard maybe couldn't fully load
         // this could then result into ugly scrollbars being visible the next time the dashboard is visible
         this.iframeProbablyCouldNotFullyLoad = currentTimestamp.getTime() - (this.iframeStartedInitializingTimestamp?.getTime() ?? 0) <= 2000;

         // we don't want to perform any other action if the window has no proper size
         return;
      }

      if (this.iframeDimensions.isZeroSize()) {
         this.reloadIframe();
      }
      if (newIframeDimensions.equals(this.iframeDimensions) && this.iframeProbablyCouldNotFullyLoad) {
         this.reloadIframe();
      }

      this.iframeStartedInitializingTimestamp = currentTimestamp;

      this.iframeDimensions = newIframeDimensions;
      this.resizeDelayTimer = null;
   }

   private getIframeDimensions(): IframeDimensions {
      return new IframeDimensions(
         // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
         this.iframe?.nativeElement?.offsetWidth ?? 0,
         // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
         this.iframe?.nativeElement?.offsetHeight ?? 0
      );
   }

   private reloadIframe(): void {
      const iframe = this.iframe.nativeElement as HTMLIFrameElement;
      iframe.src = iframe.src;
   }
}
